import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPinIcon, SearchIcon } from 'lucide-react';
import { useYmaps } from '@noor/maps';
import { useDebouncedCallback } from '@noor/lib';
import { geocoderSearch } from '../api/geocoder-api';
import type { LegacyPoint } from '../model/schema';
import type { LegacyGeoResult } from '../lib/geocode';

/**
 * Address search for a stop: type a street, pick a suggestion, get a
 * coordinate. Backed by `geocoderSearch` (the Geocoder HTTP API), bounded to
 * Tashkent exactly as the old `searchYmaps` helper was — the `rspn` window
 * keeps a bare street name from matching a same-named street in another city.
 *
 * A coordinate is what actually makes a stop submittable (see
 * `isDestinationComplete`), so the typed text alone is not enough; picking a
 * suggestion is the step that sets it.
 *
 * Pairs with the map's click-to-pick: focusing this field arms the map to fill
 * THIS stop on its next click, and both paths share `geocoderSearch` so the
 * address they produce is identical.
 *
 * Degrades honestly: with no API key the map never loads, and the control says
 * so rather than silently accepting text that can never resolve.
 */

const SEARCH_LIMIT = 6;
const SEARCH_DEBOUNCE_MS = 400;

interface LegacyAddressSearchProps {
  value: string;
  onChange: (value: string) => void;
  onPick: (address: string, point: LegacyPoint) => void;
  /** Focusing arms the map to fill THIS stop on the next click. */
  onFocus?: () => void;
  /**
   * Overrides the default «Улица, дом». Screens where the map is an equal way
   * in — the branch drawer — say so here, rather than leaving a placeholder
   * that only hints at typing.
   */
  placeholder?: string | undefined;
}

/**
 * FULLY CONTROLLED: the input renders `value` directly, with no local mirror
 * and no remount key.
 *
 * An earlier version kept a `query` state seeded from `value` and remounted the
 * field whenever the stop's COORDINATE changed. That interacted badly with the
 * parent, which clears the coordinate to `{0,0}` on every keystroke (a typed
 * address no longer names the picked place). So each character remounted the
 * component: the caret was lost after one letter and the field could not be
 * edited at all. Rendering `value` straight through removes the mirror that
 * needed re-seeding in the first place.
 */
export function LegacyAddressSearch({
  value,
  onChange,
  onPick,
  onFocus,
  placeholder,
}: LegacyAddressSearchProps) {
  const { t } = useTranslation('legacy');
  const { isDisabled } = useYmaps();

  const [results, setResults] = useState<LegacyGeoResult[]>([]);
  const [open, setOpen] = useState(false);

  const runSearch = useDebouncedCallback((text: string) => {
    if (text.trim().length < 3) {
      setResults([]);
      return;
    }

    void (async () => {
      try {
        // Shared with the map's click-to-pick, so both paths agree on what an
        // address label looks like.
        const found = await geocoderSearch(text, SEARCH_LIMIT);
        setResults(found);
        setOpen(true);
      } catch {
        // A failed lookup leaves the field usable; the merchant can retype.
        setResults([]);
      }
    })();
  }, SEARCH_DEBOUNCE_MS);

  if (isDisabled) {
    return (
      <div className="flex h-12 flex-1 items-center gap-2 rounded-[96px] bg-[var(--lg-grey-100)] px-4">
        <MapPinIcon className="size-5 shrink-0 text-[var(--lg-grey-400)]" />
        {/* `location_not_access` — the closest existing key for "the map
            service is unavailable". `create.empty_coords_error` used to sit
            here, but that string is the old panel's SUBMIT toast for a stop
            with no coordinates, and reusing it conflated the two states. */}
        <span className="text-[14px] text-[var(--lg-grey-500)]">
          {t('location_not_access')}
        </span>
      </div>
    );
  }

  return (
    <div className="relative flex-1">
      <label className="flex h-12 items-center gap-2 rounded-[96px] bg-[var(--lg-grey-100)] px-4">
        <SearchIcon className="size-5 shrink-0 text-[var(--lg-grey-500)]" />
        <input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            runSearch(e.target.value);
          }}
          onFocus={() => {
            onFocus?.();
            if (results.length > 0) setOpen(true);
          }}
          placeholder={placeholder ?? t('create.address_field')}
          className="h-full w-full bg-transparent text-[16px] font-medium text-[var(--lg-fg)] outline-none placeholder:text-[var(--lg-grey-400)]"
        />
      </label>

      {open && results.length > 0 && (
        <ul className="absolute top-full right-0 left-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-[16px] bg-[var(--lg-surface)] py-2 shadow-popover-halo">
          {results.map((result, index) => (
            <li key={`${result.address}-${index}`}>
              <button
                type="button"
                onClick={() => {
                  // `onPick` sets both the text and the coordinate upstream;
                  // the input re-renders from the new `value`.
                  onPick(result.address, result.point);
                  setOpen(false);
                }}
                className="flex w-full cursor-pointer px-4 py-2 text-left text-[14px] font-medium text-[var(--lg-fg)] transition-colors hover:bg-[var(--lg-grey-100)]"
              >
                {result.address}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
